<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import Skeleton from '@/components/Skeleton.vue'
import UIKit from '@pocketprep/ui-kit'
import { organizationModule } from '@/store/organization/module'

const {
    Link: PocketLink,
    Icon,
    Tag,
} = UIKit

const route = useRoute()

const version = import.meta.env.VUE_APP_VERSION

const showPurchasesTab = computed(() => {
    return organizationModule.getters.getHasSelfServicePaymentAccess()
})

const showIntegrationsTab = computed(() => {
    const currentOrgId = organizationModule.getters.getCurrentOrganization()?.objectId || ''

    // These are the only two orgs that use integrations so we are keeping the feature for them only:
    // Florida Southwestern College
    // Jefferson State Community College Vet Tech Program
    return [ 'jTVGmE0hSr', 'N0W93UOBR0' ].includes(currentOrgId)
})
</script>

<template>
    <Skeleton class="admin">
        <template #side>
            <ul class="admin__navigation">
                <li>
                    <RouterLink :to="{ name: 'organization' }">
                        Organization
                    </RouterLink>
                    <ul v-if="route.name === 'organization'">
                        <li>
                            <a href="#students">
                                Students
                            </a>
                        </li>
                        <li>
                            <a href="#groups">
                                Groups
                            </a>
                        </li>
                        <li>
                            <a href="#admin-instructors">
                                Admin &amp; Instructors
                            </a>
                        </li>
                    </ul>
                </li>
                <li v-if="showIntegrationsTab">
                    <RouterLink :to="{ name: 'integrations' }">
                        Integrations
                        <Tag
                            :is-beta-tag="true"
                            class="admin__beta-tag"
                            size="small"
                        >
                            <template #tagMessage>
                                Beta
                            </template>
                        </Tag>
                    </RouterLink>
                </li>
                <li v-if="showPurchasesTab">
                    <RouterLink :to="{ name: 'purchases' }">
                        Purchases
                    </RouterLink>
                </li>
                <li>
                    <RouterLink :to="{ name: 'account' }">
                        Account
                    </RouterLink>

                    <ul v-if="route.name === 'account'">
                        <li>
                            <RouterLink :to="{ name: 'account', hash: '#name-email' }">
                                Name &amp; Email
                            </RouterLink>
                        </li>
                    </ul>
                </li>
            </ul>
            <div class="admin__links">
                <PocketLink
                    target="_blank"
                    href="https://help.educators.pocketprep.com"
                    class="admin__links-help"
                >
                    <Icon type="help" />
                    Help
                </PocketLink>
                <PocketLink
                    target="_blank"
                    href="http://www.pocketprep.com/terms-of-service/"
                    class="admin__links-terms"
                >
                    <Icon type="lock" />
                    Privacy Policy &amp; Terms of Use
                </PocketLink>
            </div>
            <div v-if="version" class="admin__version">
                v{{ version }}
            </div>
        </template>

        <RouterView />
    </Skeleton>
</template>

<style lang="scss" scoped>


.admin {
    &__navigation {
        margin: 51px 45px 0;
        padding: 0;

        li {
            margin-bottom: 16px;
            list-style: none;
            padding: 0;

            &:last-child {
                margin-bottom: 0;
            }

            a {
                font-size: 16px;
                line-height: 22px;
                font-weight: 600;
                color: $slate-01;
                text-decoration: none;

                &.router-link-active {
                    color: $brand-black;
                }
            }

            ul {
                margin: 17px 0 0;
                padding: 0;

                li {
                    margin: 0 0 12px 19px;
                    padding: 0;

                    &:last-child {
                        margin-bottom: 0;
                    }

                    a {
                        color: $brand-black;
                        font-size: 15px;
                        line-height: 20px;
                        font-weight: 500;
                    }
                }
            }
        }
    }

    &__beta-tag {
        margin-left: 3px;
    }

    &__links {
        position: fixed;
        left: 45px;
        bottom: 100px;
        height: 63px;

        a {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
            font-size: 14px;
            line-height: 19px;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    &__links-help svg {
        width: 15px;
        margin-right: 10px;
        margin-left: 3px;
    }

    &__links-terms svg {
        width: 21px;
        margin-right: 7px;
    }

    &__version {
        position: fixed;
        left: 45px;
        bottom: 76px;
        color: $slate-01;
        font-size: 15px;
    }
}
</style>
